/**
 * 路線形状 GeoJSON のフェッチ。
 *
 * - 形状ファイルは `web-app/public/data/route_shapes/` に置かれ、`/data/route_shapes/` で配信される。
 *   正本は OBA-Transformer の Release (`route-master-v*`) で、`scripts/sync_route_master.py --update`
 *   が取り込む (docs/adr-route-master-distribution.md §10-3)。手で編集しない。
 * - `(routeId, directionName)` や `routeId` からファイル名を引く対応表は、コードに持たない。
 *   同じ場所のマニフェスト `route_shapes_manifest.json` (`shapes[]`) を読んで引く。
 *   路線や方面を足すときにこのファイルを直す必要はない。
 * - 方面ごとの線形 (`shape_kind: 'points'`) は GPS 観測点をサンプルした **Point** ジオメトリで、
 *   properties に GPS 精度 (`gps_accuracy_avg/min/max`) やアナウンス有無 (`is_announcing`) などの
 *   計測値を持つ。路線の線形 (`shape_kind: 'line'`) は方面に依存しない。
 * - マニフェストを取得できない・形が想定外・該当が無い場合は `null` を返し、呼び出し側で
 *   「線形データを表示しない」仕様とする。
 *
 * Issue #231: 以前は `${route_id}_${direction_name}.geojson` (日本語ファイル名) としていたが、
 *   CDK `BucketDeployment` の内蔵 Lambda がロケール `C` で起動されるため AWS CLI (`aws s3 sync`) が
 *   UnicodeEncodeError で失敗する。そのためファイル名は ASCII のみにする。マニフェストの `file` も
 *   ASCII の安全な名前だけを受け付け (`SAFE_SHAPE_FILE`)、それ以外は URL に使わない。
 */

/**
 * route_shapes/*.geojson の各 Feature の properties。
 *
 * すべての値は欠損 (null) する可能性がある (まだ計測点が取れていない時)。
 */
export interface RouteShapePointProperties {
  route?: string;
  direction?: string;
  from_station?: string;
  to_station?: string;
  sequence?: number;
  /** GPS 精度 (m) の平均。値が小さいほど高精度。 */
  gps_accuracy_avg: number | null;
  gps_accuracy_min: number | null;
  gps_accuracy_max: number | null;
  /** GPS マッチした計測サンプル数 */
  gps_match_count: number | null;
  /**
   * 当該地点でアナウンスが行われた実績。
   * - `true`: アナウンス区間 (赤系で表示)
   * - `false`: 非アナウンス区間 (灰系で表示)
   * - `null`: 未取得 (薄灰で表示)
   */
  is_announcing: boolean | null;
  recorded_at: string | null;
}

export interface RouteShapePointFeature {
  type: 'Feature';
  properties: RouteShapePointProperties;
  geometry: {
    type: 'Point';
    /** [lng, lat] (GeoJSON 規約) */
    coordinates: [number, number];
  };
}

/** 方面ごとの Point Feature コレクション */
export interface RouteShapePointsFile {
  type: 'FeatureCollection';
  name?: string;
  features: RouteShapePointFeature[];
}

// ---------------------------------------------------------------------------
// マニフェスト (route_shapes_manifest.json)
// ---------------------------------------------------------------------------

/** マニフェストの配信パス。 */
const ROUTE_SHAPES_BASE_PATH = '/data/route_shapes';
const MANIFEST_URL = `${ROUTE_SHAPES_BASE_PATH}/route_shapes_manifest.json`;

/** 対応するマニフェストの `schema_version`。違う版は読まない (描画しない側に倒す)。 */
const SUPPORTED_MANIFEST_SCHEMA_VERSION = 1;

/**
 * URL に使ってよいファイル名。ASCII の英数字で始まり、`/` や `..` を含められない。
 * 配信側 (Transformer の検査、`sync_route_master.py`) と同じ規則。
 */
const SAFE_SHAPE_FILE = /^[A-Za-z0-9][A-Za-z0-9._-]*\.geojson$/;

/** マニフェストの 1 件。`points` は方面ごとの観測点、`line` は路線の線形。 */
export type RouteShapeManifestEntry =
  | {
      route_id: string;
      direction_name: string;
      shape_kind: 'points';
      file: string;
      sha256: string;
    }
  | {
      route_id: string;
      shape_kind: 'line';
      file: string;
      sha256: string;
    };

export interface RouteShapeManifest {
  schema_version: 1;
  shapes: RouteShapeManifestEntry[];
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v !== '';
}

/**
 * 1 件を検証する。形が想定外なら `null` (その 1 件だけを無視する)。
 * `file` は URL に使うので `SAFE_SHAPE_FILE` に合うものだけを通す。
 */
function parseManifestEntry(raw: unknown): RouteShapeManifestEntry | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Record<string, unknown>;
  if (!isNonEmptyString(e.route_id)) return null;
  if (typeof e.file !== 'string' || !SAFE_SHAPE_FILE.test(e.file)) return null;
  if (typeof e.sha256 !== 'string') return null;
  if (e.shape_kind === 'points') {
    if (!isNonEmptyString(e.direction_name)) return null;
    return {
      route_id: e.route_id,
      direction_name: e.direction_name,
      shape_kind: 'points',
      file: e.file,
      sha256: e.sha256,
    };
  }
  if (e.shape_kind === 'line') {
    return { route_id: e.route_id, shape_kind: 'line', file: e.file, sha256: e.sha256 };
  }
  return null;
}

/**
 * マニフェストを検証する。`schema_version` が違う、または `shapes` が配列でないときは `null`。
 * 配列の中の不正な 1 件は、その 1 件だけを無視する。
 */
export function parseRouteShapeManifest(json: unknown): RouteShapeManifest | null {
  if (typeof json !== 'object' || json === null) return null;
  const m = json as Record<string, unknown>;
  if (m.schema_version !== SUPPORTED_MANIFEST_SCHEMA_VERSION) return null;
  if (!Array.isArray(m.shapes)) return null;
  const shapes = m.shapes
    .map(parseManifestEntry)
    .filter((e): e is RouteShapeManifestEntry => e !== null);
  return { schema_version: SUPPORTED_MANIFEST_SCHEMA_VERSION, shapes };
}

/** 取得に成功したマニフェスト。失敗 (`null`) は保持せず、次の呼び出しで取り直す。 */
let manifestPromise: Promise<RouteShapeManifest | null> | null = null;

async function loadRouteShapeManifest(): Promise<RouteShapeManifest | null> {
  try {
    const res = await fetch(MANIFEST_URL, { cache: 'no-store' });
    if (!res.ok) return null;
    return parseRouteShapeManifest(await res.json());
  } catch {
    return null;
  }
}

/**
 * マニフェストを取得する。同時の呼び出しは 1 回の取得を共有し、成功した結果は保持する。
 * 取得に失敗したとき (`null`) は保持しないので、次の呼び出しで取り直す。
 */
export function fetchRouteShapeManifest(): Promise<RouteShapeManifest | null> {
  if (!manifestPromise) {
    const pending = loadRouteShapeManifest().then((manifest) => {
      if (manifest === null && manifestPromise === pending) manifestPromise = null;
      return manifest;
    });
    manifestPromise = pending;
  }
  return manifestPromise;
}

/** 保持したマニフェストを捨てる。ユニットテスト用。 */
export function resetRouteShapeManifestCache(): void {
  manifestPromise = null;
}

/**
 * `(routeId, directionName)` に対応する方面ごとの観測点ファイル名 (拡張子を含む) を返す。
 * 該当が無い・引数が空なら `null`。
 *
 * export しているのはユニットテスト用途。
 */
export function resolveRouteShapeFile(
  manifest: RouteShapeManifest,
  routeId: string,
  directionName: string,
): string | null {
  if (!routeId || !directionName) return null;
  const entry = manifest.shapes.find(
    (e) =>
      e.shape_kind === 'points' && e.route_id === routeId && e.direction_name === directionName,
  );
  return entry?.file ?? null;
}

// ---------------------------------------------------------------------------
// 路線の線形 (LineString) — 線区カラーで塗る路線図
// ---------------------------------------------------------------------------

/**
 * 路線の線形 (LineString / MultiLineString) の Feature。
 *
 * GPS 実測の Point 集合 (`RouteShapePointFeature`) とは別物で、国土数値情報
 * (N02 鉄道データ) 等から得た **線路の形そのもの** を表す。方面の区別は無く、
 * 地図上に線区カラー (routes-master.json の `route_color`) で描画する。
 * properties は描画に使わないため型を固定しない (N02 形式なら `N02_003` = 路線名など)。
 */
export interface RouteLineFeature {
  type: 'Feature';
  properties?: Record<string, unknown> | null;
  geometry:
    | {
        type: 'LineString';
        /** [lng, lat][] (GeoJSON 規約) */
        coordinates: [number, number][];
      }
    | {
        type: 'MultiLineString';
        coordinates: [number, number][][];
      };
}

/** 路線の線形 Feature コレクション */
export interface RouteLineFile {
  type: 'FeatureCollection';
  name?: string;
  features: RouteLineFeature[];
}

/**
 * `routeId` に対応する路線線形のファイル名 (拡張子を含む) を返す。方面に依存しない
 * (上下線で同じ線路を描く) ため、キーは route_id のみ。該当が無い・空なら `null`。
 *
 * export しているのはユニットテスト用途。
 */
export function resolveRouteLineFile(manifest: RouteShapeManifest, routeId: string): string | null {
  if (!routeId) return null;
  const entry = manifest.shapes.find((e) => e.shape_kind === 'line' && e.route_id === routeId);
  return entry?.file ?? null;
}

/** 座標 1 点が [lng, lat] の有限数値か */
function isLngLat(c: unknown): c is [number, number] {
  return (
    Array.isArray(c) &&
    c.length >= 2 &&
    typeof c[0] === 'number' &&
    typeof c[1] === 'number' &&
    Number.isFinite(c[0]) &&
    Number.isFinite(c[1])
  );
}

/**
 * 路線線形 Feature を「折れ線 (座標列) の配列」に変換する。
 * - LineString は 1 本、MultiLineString は構成線の本数ぶん返す。
 * - 2 点未満の線・不正な座標はスキップする。
 * - 返す座標は Leaflet 向けの **[lat, lng]** 順 (GeoJSON の [lng, lat] から入れ替える)。
 */
export function routeLineToLatLngs(file: RouteLineFile | null | undefined): [number, number][][] {
  if (!file || !Array.isArray(file.features)) return [];
  const lines: [number, number][][] = [];
  const push = (coords: unknown): void => {
    if (!Array.isArray(coords)) return;
    const latLngs = coords.filter(isLngLat).map(([lng, lat]) => [lat, lng] as [number, number]);
    if (latLngs.length >= 2) lines.push(latLngs);
  };
  for (const f of file.features) {
    const g = f?.geometry;
    if (!g) continue;
    if (g.type === 'LineString') push(g.coordinates);
    else if (g.type === 'MultiLineString' && Array.isArray(g.coordinates)) {
      g.coordinates.forEach(push);
    }
  }
  return lines;
}

/** 形状ファイル 1 つを取得して FeatureCollection であることを確かめる。取れない・形が違うなら `null`。 */
async function loadShapeFile<T extends { type: string; features: unknown }>(
  file: string,
): Promise<T | null> {
  // file は SAFE_SHAPE_FILE (ASCII のみ) を通っているので encodeURIComponent は不要だが、
  // 将来別の記号が混入しても壊れないよう念のためエンコードしておく。
  const url = `${ROUTE_SHAPES_BASE_PATH}/${encodeURIComponent(file)}`;
  try {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;
    const json = (await res.json()) as T;
    if (!json || json.type !== 'FeatureCollection' || !Array.isArray(json.features)) {
      return null;
    }
    return json;
  } catch {
    return null;
  }
}

/**
 * マニフェストでファイル名を引き、形状ファイルを取得する。
 *
 * 取得に失敗したときは、保持していたマニフェストを捨てて取り直し、ファイル名が変わっていれば
 * 1 回だけ取り直す。画面を開いたままで形状ファイルを改名した版がデプロイされると、
 * `prune` で旧名が消える一方で、保持した旧マニフェストは旧名を指し続けるため。
 *
 * 失敗の種類は分けない。CloudFront は 403/404 を `/index.html` の 200 に変える (SPA 用) ので、
 * 消えた旧名を取りに行っても 404 ではなく HTML が返り、JSON のパース失敗として現れる。
 */
async function fetchResolvedShapeFile<T extends { type: string; features: unknown }>(
  resolveFile: (manifest: RouteShapeManifest) => string | null,
): Promise<T | null> {
  const used = fetchRouteShapeManifest();
  const manifest = await used;
  if (!manifest) return null;
  const file = resolveFile(manifest);
  if (!file) return null;

  const first = await loadShapeFile<T>(file);
  if (first) return first;

  // すでに他の呼び出しが取り直しているなら、それを共有する (新しい取得を捨てない)。
  if (manifestPromise === used) manifestPromise = null;
  const fresh = await fetchRouteShapeManifest();
  if (!fresh) return null;
  const retryFile = resolveFile(fresh);
  if (!retryFile || retryFile === file) return null;
  return loadShapeFile<T>(retryFile);
}

/**
 * 指定した route_id の路線線形 GeoJSON を取得する。
 * - マニフェストに無い / 404 / JSON 不正の場合は `null` (= 路線を描画しない)。
 */
export async function fetchRouteLine(routeId: string): Promise<RouteLineFile | null> {
  if (!routeId) return null;
  return fetchResolvedShapeFile<RouteLineFile>((manifest) =>
    resolveRouteLineFile(manifest, routeId),
  );
}

/**
 * 指定した route_id / direction_name に対応する線形 GeoJSON を取得する。
 *
 * - 404 / JSON パースエラーなど取得不可の場合は `null` を返す。
 *   呼び出し側 (地図) は `null` の時に線形を描画しないこと。
 * - マニフェストに無い路線・方面もファイル未配布とみなし `null` を返す。
 *
 * @param routeId        例: "中総緩"
 * @param directionName  例: "A線（千葉方面）"
 */
export async function fetchRouteShapePoints(
  routeId: string,
  directionName: string,
): Promise<RouteShapePointsFile | null> {
  if (!routeId || !directionName) return null;
  return fetchResolvedShapeFile<RouteShapePointsFile>((manifest) =>
    resolveRouteShapeFile(manifest, routeId, directionName),
  );
}
